import React, { useState, useEffect } from "react";
import {
  TextField,
  Button,
  Box,
  Toolbar,
  Menu,
  MenuItem,
  Fab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { ThemeProvider } from "@mui/material/styles";
import FeedbackIcon from "@mui/icons-material/Feedback";
import { v4 as uuidv4 } from 'uuid';

import theme from "./theme/theme";

// Components
import SearchSection from "./components/SearchSection";
import FeaturesSection from "./components/FeaturesSection";
import AuthDialog from "./components/AuthDialog";
import AboutSection from "./components/AboutSection";
import FeedbackSection from "./components/FeedbackSection";
import Header from "./components/Header";
import Impressum from "./components/Impressum";
import Privacy from "./components/Privacy";
import CookieBanner from "./components/CookieBanner";

// Context
import { useAuth } from "./context/AuthContext";

import { backendServerUrl } from "./config";

function App() {
  const [tabIndex, setTabIndex] = useState(0); // Add state for tab index
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [answer, setAnswer] = useState("");
  const [loading, setLoading] = useState(false);
  const [previousSearches, setPreviousSearches] = useState([]);
  const [answerLoading, setAnswerLoading] = useState(false); // New loading state for LLM-generated responses

  // For authentication modal
  const [authModalOpen, setAuthModalOpen] = useState(false);
  const { user, isLoggedIn, login, logout } = useAuth();
  const [profileMenuAnchor, setProfileMenuAnchor] = useState(null);

  // For previous searches menu
  const [anchorEl, setAnchorEl] = useState(null);

  // For previous searches menu
  useEffect(() => {
    if (isLoggedIn) {
      fetchUserPreviousSearches();
    }
  }, [isLoggedIn]);

  async function fetchUserPreviousSearches() {
    try {
      const token = localStorage.getItem("authToken");
      const response = await fetch(`${backendServerUrl}/api/previous-searches`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error("Failed to fetch previous searches");
      }

      const data = await response.json();
      const searchQueries = data.map(item => item.search_query);

      setPreviousSearches(searchQueries);
    } catch (error) {
      console.error("Error fetching user previous searches:", error);
    }
  }

  async function storeSearchQuery(searchId, email, search_query) {
    try {
      const token = localStorage.getItem("authToken");
      const response = await fetch(`${backendServerUrl}/api/previous-searches`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ search_id: searchId, email, search_query }),
      });

      if (!response.ok) {
        throw new Error("Failed to store the new search query");
      }
      console.log("Search query saved in DB successfully.");
    } catch (error) {
      console.error("Error saving the new search query to DB:", error);
    }
  }

  const handleOpenMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };
  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  // Smooth scroll to a specific section by ID
  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  // For Search
  const [searchId, setSearchId] = useState(uuidv4());

  async function handleSearch(e, searchScope) {
    e.preventDefault();
    setLoading(true);
    setAnswerLoading(true); // Set answer loading to true
    setResults([]);
    setAnswer("");

    if (!isLoggedIn) {
      setAuthModalOpen(true);
      setLoading(false);
      setAnswerLoading(false);
      return;
    }

    try {
      const token = localStorage.getItem("authToken");

      // Step 1: Fetch search results
      const searchResponse = await fetch(`${backendServerUrl}/api/search`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ query }),
      });

      if (!searchResponse.ok) {
        throw new Error("Search request failed");
      }

      const searchResults = await searchResponse.json();
      storeSearchQuery(searchId, user.email, query);
      setResults(searchResults);
      setLoading(false);

      // Step 2: Stream QA responses
      const qaStreamResponse = await fetch(`${backendServerUrl}/api/qa-stream`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ query }),
      });

      if (!qaStreamResponse.ok || !qaStreamResponse.body) {
        throw new Error("QA stream response failed");
      }

      const reader = qaStreamResponse.body.getReader();
      const decoder = new TextDecoder("utf-8");
      let buffer = "";

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        let parts = buffer.split("\n\n");

        // Keep the last partial piece in buffer (might not be a full chunk yet)
        buffer = parts.pop();

        for (let part of parts) {
          const lines = part.trim().split("\n");
          let eventType = "";
          let jsonData = "";

          for (const line of lines) {
            if (line.startsWith("event: ")) {
              eventType = line.replace("event: ", "").trim();
            } else if (line.startsWith("data: ")) {
              jsonData += line.replace("data: ", "").trim();
            } else {
              jsonData += line.trim(); // handle multiline JSON
            }
          }

          if (eventType === "qaToken") {
            const { token } = JSON.parse(jsonData);
            setAnswerLoading(false);
            setAnswer((prev) => prev + token);
          } else if (eventType === "error") {
            const { error } = JSON.parse(jsonData);
            console.error("Error from QA stream:", error);
          }
        }
      }

      if (query && !previousSearches.includes(query)) {
        setPreviousSearches((prev) => [query, ...prev]);
      }
      // Refresh search ID for a new search
      setSearchId(uuidv4());
    } catch (error) {
      console.error("Search failed:", error);
      setResults([]); // Reset results to an empty array on error
    } finally {
      setLoading(false);
      setAnswerLoading(false); // Reset answer loading
    }
  }

  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [feedbackText, setFeedbackText] = useState("");

  const handleFeedbackClick = () => {
    if (!isLoggedIn) {
      setAuthModalOpen(true);
      return;
    }
    setFeedbackOpen(true);
  };

  const handleFeedbackSubmit = async () => {
    try {
      const token = localStorage.getItem("authToken");
      const response = await fetch(`${backendServerUrl}/api/feedback`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          search_id: searchId,
          feedback_text: feedbackText,
          email: user?.email,
          last_search_query: query,
          last_search_results: JSON.stringify(results),
        }),
      });

      if (!response.ok) {
        throw new Error("Failed to submit feedback");
      }

      alert("Feedback submitted successfully!");
      setFeedbackText("");
      setFeedbackOpen(false);
    } catch (error) {
      console.error("Error submitting feedback:", error);
      alert("An error occurred while submitting feedback.");
    }
  };

  return (
    <ThemeProvider theme={theme}>
      {/* Header */}
      <Header
        tabIndex={tabIndex}
        setTabIndex={setTabIndex}
        isLoggedIn={isLoggedIn}
        setAuthModalOpen={setAuthModalOpen}
        setProfileMenuAnchor={setProfileMenuAnchor}
        scrollToSection={scrollToSection}
      />
      <Toolbar /> {/* Spacer to avoid content being hidden under the AppBar */}
      {/* Tab Content */}
      {tabIndex === 0 && (
          <SearchSection
            query={query}
            setQuery={setQuery}
            previousSearches={previousSearches}
            anchorEl={anchorEl}
            handleOpenMenu={handleOpenMenu}
            handleCloseMenu={handleCloseMenu}
            handleSearch={handleSearch}
            results={results}
            loading={loading}
            answer={answer}
            answerLoading={answerLoading}
            searchId={searchId}
          />
      )}
      {tabIndex === 1 && (
          <FeaturesSection />
      )}
      {tabIndex === 2 && (
          <AboutSection />
      )}
      {tabIndex === 3 && (
          <FeedbackSection />
      )}
      {tabIndex === 4 && (
          <Impressum />
      )}
      {tabIndex === 5 && (
          <Privacy />
      )}
      <AuthDialog
        open={authModalOpen}
        onClose={() => setAuthModalOpen(false)}
        onLoginSuccess={(email) => {
          login(email);
          setAuthModalOpen(false);
        }}
      />
      <Menu
        anchorEl={profileMenuAnchor}
        open={Boolean(profileMenuAnchor)}
        onClose={() => setProfileMenuAnchor(null)}
      >
        <MenuItem disabled>{user?.email}</MenuItem>
        <MenuItem
          onClick={() => {
            logout();
            setProfileMenuAnchor(null);
          }}
        >
          Logout
        </MenuItem>
      </Menu>

      {/* Floating Feedback Button */}
      <Fab
        color="secondary"
        aria-label="feedback"
        onClick={handleFeedbackClick}
        style={{
          position: "fixed",
          bottom: 16,
          right: 16,
          zIndex: 9999,
        }}
      >
        <FeedbackIcon />
      </Fab>

      {/* Feedback Dialog */}
      <Dialog open={feedbackOpen} onClose={() => setFeedbackOpen(false)}>
        <DialogTitle>Submit Feedback</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            multiline
            rows={4}
            variant="outlined"
            label="Your Feedback"
            value={feedbackText}
            onChange={(e) => setFeedbackText(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFeedbackOpen(false)}>Cancel</Button>
          <Button onClick={handleFeedbackSubmit} variant="contained" color="primary">
            Submit
          </Button>
        </DialogActions>
      </Dialog>
      <CookieBanner />
    </ThemeProvider>
  );
}

export default App;
